import {useCallback, useMemo} from 'react';

import {ProductLevel} from 'modules/decisionsNext/api/types';
import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';

import {
  GenericTreeNode,
  groupSelectedKeys,
  groupSelectedKeysByLevel,
  hasChildren,
  iterateTree,
  transformTree,
  ungroupSelectedKeys,
} from 'utils/treeUtils';
import {useProductTreeQuery} from 'modules/decisionsNext/api/trees/productTree.generated';
import {productFilterKeys, ProductFilterKeys} from 'core/constants/policyFilter';

import {selectedByLevelToFilter} from 'components/TreePicker/utils/filterTreePickerUtils';
import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

type ProductNode = GenericTreeNode<TreeBasicNode & {level: ProductLevel}>;

export const useProductTree: UseTreeData<string, ProductNode, SkipQueryParams & {roleId?: string}> = ({
  skip,
  roleId,
} = {}) => {
  const {data, loading} = useProductTreeQuery({
    skip,
    variables: {roleId},
    disableSkipSetsLoadingState: true,
  });

  const treeData = useMemo(
    () =>
      transformTree(data?.products?.treeRestricted ?? [], item => ({
        title: item.name,
        key: item.id as (typeof productFilterKeys)[number],
        level: item.level,
      })),
    [data?.products?.treeRestricted]
  );

  const treeMap = useMemo(() => {
    const map = new Map<string, ProductNode>();

    iterateTree(
      treeData,
      (node, parent) => {
        map.set(node.key, {
          key: node.key,
          title: node.title,
          parentKey: parent?.key,
          level: node.level,
          childrenCount: hasChildren(node) ? node.children.length : undefined,
        });
      },
      {withParent: true}
    );

    return map;
  }, [treeData]);

  return {
    treeData,
    treeMap,
    loading,
  };
};

export function useProductTreeSelection(
  treeData: GenericTreeNode<TreeBasicNode>[],
  {store, setStore}: StoreAdapterParams<ProductFilterKeys>
) {
  const {productInsuranceSegmentIds, productSuperGroupIds, productGroupIds, productIds} = store;
  const selected = useMemo(() => {
    const allIds = [productInsuranceSegmentIds, productSuperGroupIds, productGroupIds, productIds].flatMap(
      group => group || []
    );
    return Array.from(ungroupSelectedKeys(treeData, allIds));
  }, [productInsuranceSegmentIds, productSuperGroupIds, productGroupIds, productIds, treeData]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = groupSelectedKeys(treeData, newSelected);
      const selectedByLevel = groupSelectedKeysByLevel(treeData, groupedSelected);
      setStore(
        selectedByLevelToFilter(selectedByLevel, [
          'productInsuranceSegmentIds',
          'productSuperGroupIds',
          'productGroupIds',
          'productIds',
        ])
      );
    },
    [setStore, treeData]
  );

  const setSelectedNone = useCallback(() => {
    setStore({
      productInsuranceSegmentIds: undefined,
      productSuperGroupIds: undefined,
      productGroupIds: undefined,
      productIds: undefined,
    });
  }, [setStore]);

  return {
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
    }),
    selectedNoneData: {
      productInsuranceSegmentIds: undefined,
      productSuperGroupIds: undefined,
      productGroupIds: undefined,
      productIds: undefined,
    },
  };
}
